import React, { ForwardedRef, ReactElement, ReactNode, useCallback, useMemo, useState } from 'react';
import type { BaseRowData, DataProvider } from '@adv-frontend/data-provider';
import { type UseAsyncFlowResult, useAsyncFlow } from '@adv-frontend/modules-flow';
import { Virtualizer } from '@react-aria/virtualizer';
import { getPlatformStyles, isDesktop, isMobile } from '@direct-frontend/platform-code';
import { Item, useListState } from 'react-stately';
import { ListLayout, ListLayoutOptions } from '@react-stately/layout';
import { genericForwardRef, mergeRefs, useOnce, useResizeListener } from '@direct-frontend/stdlib';

import { ListStubItem } from '../ListStubItem';
import { ListSize } from '../../ListBoxBase/types';
import { List, ListProps } from '../List';
import { getScrollbarClassName } from '../../../lib/css/getScrollbarClassName';
import { useVStack } from '../../../hooks/useStack';
import { ListSection } from '../ListSection';
import { cn } from '../../../lib/css/className/className';
import { Separator } from '../../ListBoxBase/Separator/Separator';
import { VirtualListItemContext } from '../utils';

getPlatformStyles({
    common: import('./VirtualList.css'),
    mobile: import('./VirtualList@mobile.css'),
});

const cls = cn('VirtualList');
const defaultLoadingItem = <ListStubItem width="100%" />;

export interface VirtualListProps<RowData extends BaseRowData = BaseRowData>
    extends Omit<ListProps, 'children' | 'showSeparator' | 'search' | 'searchEmptyText'> {
    /**
     * dataProvider для элементов
     * @link https://nda.ya.ru/t/S1VBR-zr74tgTc
     */
    dataProvider: DataProvider<RowData>;

    /**
     * Функция для отрисовки строки с данными или ошибкой.
     * При первичной загрузке и смене потока без данных отображается loadingItem.
     * @param itemState состояние строки flow-based dataProvider
     * @returns верстка элемента
     * @example itemState => <ListItem id={itemState.current().id}>{itemState.current().name}</ListItem>
     * @expectedNode `ListItem` | `ListCustomItem` | `ListInputItem` |
     *               `ListStubItem` | `ListTreeItem` | `ListSubmenuItem`
     */
    children: (itemState: UseAsyncFlowResult<RowData>) => ReactElement | null;

    /**
     * Элемент для строки, данные которой ещё не загружены.
     * При обновлении строки с данными продолжает вызываться children.
     * @default <ListStubItem width="100%" />
     * @expectedNode `ListItem` | `ListCustomItem` | `ListInputItem` |
     *               `ListStubItem` | `ListTreeItem` | `ListSubmenuItem`
     */
    loadingItem?: ReactElement | null;

    /**
     * Элемент, который нужно показать, если dataProvider не вернул ни один элемент
     * @example placeholder={<ListItem>Ничего не нашлось</ListItem>}
     */
    placeholder?: ReactNode;

    /**
     * Максимальная высота списка
     * Рекомендуется передавать, если список может быть большим
     * @example maxHeight={400}
     */
    maxHeight?: number;
}

/**
 * Высота строки соответствует высоте TextInput: https://a.yandex-team.ru/arc_vcs/adv/frontend/packages/components/src/components/TextInput/TextInput.css?rev=4d49c38bd04f65c8cfeb03488487121f1982fbca#L137
 */
const rowHeightFromSize: {
    desktop: Record<ListSize, number>;
    mobile: Record<Extract<ListSize, 'm'>, number>;
} = {
    desktop: {
        m: 44,
        s: 36,
        xs: 32,
    },
    /**
     * Высота с учётом сепаратора: https://a.yandex-team.ru/arc_vcs/adv/frontend/packages/components/src/components/ListBoxBase/ListBoxBase@mobile.css?rev=4d49c38bd04f65c8cfeb03488487121f1982fbca#L45.
     * Размер только 'm' для мобильной версии.
     */
    mobile: {
        m: 61,
    },
};

interface ItemRendererProps<RowData extends BaseRowData> extends Pick<VirtualListProps<RowData>, 'dataProvider'> {
    /** Функция отрисовки загруженной строки. */
    renderer: VirtualListProps<RowData>['children'];
    /** Элемент для строки без загруженных данных. */
    loadingItem: ReactElement | null;
    /** Идентификатор виртуальной строки в DataProvider. */
    rowId: string;
    /** Является ли строка первой в списке. */
    isFirst: boolean;
    /** Является ли строка последней в списке. */
    isLast: boolean;
    /** Нужно ли показывать разделитель после строки. */
    withSeparator: boolean;
}

const ItemRenderer = function ItemRenderer<RowData extends BaseRowData>(props: ItemRendererProps<RowData>) {
    const { rowId, isFirst, isLast, withSeparator, renderer, loadingItem, dataProvider } = props;
    const contextValue = useMemo(() => ({ isFirst, isLast }), [isFirst, isLast]);
    const itemState = useAsyncFlow(dataProvider.rowData(rowId));
    const isLoading = itemState.isLoading || itemState.isStale;

    if (isLoading && loadingItem === defaultLoadingItem) {
        return loadingItem;
    }

    const renderedItem = isLoading ? loadingItem : renderer(itemState);

    if (isDesktop()) {
        return renderedItem;
    }

    return (
        <>
            <VirtualListItemContext.Provider value={contextValue}>{renderedItem}</VirtualListItemContext.Provider>
            {withSeparator && <Separator className={cls('separator')} />}
        </>
    );
};

/**
 * Компонент для создания виртуальных списков, принимает dataProvider и функцию-рендерер элементов
 *
 * Можно использовать статический массив или получать элементы из rtk api
 * @example
 * const dataProvider = createStaticDataProvider(data);
 *
 * return (
 *   <VirtualList dataProvider={dataProvider}>
 *     {itemState => <ListItem>{itemState.current().name}</ListItem>}
 *   </VirtualList>
 * );
 *
 * @example
 * const dataProvider = api.useDataProvider();
 *
 * return (
 *   <VirtualList dataProvider={dataProvider}>
 *     {itemState => <ListItem>{itemState.current().name}</ListItem>}
 *   </VirtualList>
 * );
 */
export const VirtualList = genericForwardRef(function VirtualList<RowData extends BaseRowData = BaseRowData>(
    props: VirtualListProps<RowData>,
    ref: ForwardedRef<HTMLUListElement>,
) {
    const {
        size,
        children,
        loadingItem = defaultLoadingItem,
        dataProvider,
        pinnedSection,
        pinnedSectionAfter,
        placeholder,
        className,
        'data-testid': dataTestId = 'List',
        ...listProps
    } = props;
    const scrollClass = getScrollbarClassName();
    const totalCount = useAsyncFlow(dataProvider.totalCount)();
    const rowsIds = useAsyncFlow(dataProvider.rowsIds)();

    const virtualizer = useMemo<ListLayoutOptions<BaseRowData>>(() => {
        const rowHeight = isMobile() ? rowHeightFromSize.mobile.m : rowHeightFromSize.desktop[size];

        return {
            estimatedRowHeight: rowHeight,
        };
    }, [size]);
    const listState = useListState<BaseRowData>({
        children: rowsIds.map(rowId => <Item key={rowId}>{rowId}</Item>),
    });
    const layout = useMemo(() => new ListLayout<BaseRowData>(virtualizer), [virtualizer]);
    layout.collection = listState.collection;
    const stackClass = useVStack({});

    const showPlaceholder = Boolean(placeholder) && totalCount === 0;
    const showSeparator = totalCount !== 0;

    const [listWidth, setListWidth] = useState(0);
    const resizeObserverCallback = useCallback((entries: ResizeObserverEntry[]) => {
        // Хак, чтобы перерисовывать Virtualizer при первом изменении ширины контейнера
        setListWidth(entries[0]?.contentRect.width ?? 0);
    }, []);
    const onceResizeObserverCallback = useOnce(resizeObserverCallback);
    const setListRef = useResizeListener(onceResizeObserverCallback);

    return (
        <List
            {...listProps}
            data-testid={dataTestId}
            ref={mergeRefs(ref, setListRef)}
            className={cls('', [stackClass, className])}
            size={size}
            pinnedSection={pinnedSection}
            pinnedSectionAfter={pinnedSectionAfter}
            showSeparator={showSeparator}
            isVirtualList
        >
            {showPlaceholder && <ListSection>{placeholder}</ListSection>}
            {layout.collection.size > 0 && (
                <Virtualizer
                    key={listWidth}
                    className={cls('list', [scrollClass])}
                    style={{ maxHeight: listProps.maxHeight }}
                    sizeToFit="height"
                    scrollDirection="vertical"
                    layout={layout}
                    collection={listState.collection}
                    data-testid={`${dataTestId}.ScrollContainer`}
                >
                    {(type, node) =>
                        type === 'item' ? (
                            <ItemRenderer
                                dataProvider={dataProvider}
                                rowId={String(node.key)}
                                isFirst={node.index === 0}
                                isLast={node.index === totalCount - 1}
                                withSeparator={Boolean(totalCount) && node.index !== totalCount - 1}
                                renderer={children}
                                loadingItem={loadingItem}
                            />
                        ) : null
                    }
                </Virtualizer>
            )}
        </List>
    );
});
